import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import { PencilSimple, Plus, TrashSimple } from '@phosphor-icons/react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addParameter,
  setFunctionName,
  setFunctionDescription,
  ParameterType,
  FunctionParameter,
  addParameterMapping,
  ParameterMapping,
  setApiMethod,
  setApiEndpoint,
  setProcessingMessage,
  addQueryParam,
  updateQueryParam,
  updateParameterMapping,
  addResultMapping,
  updateResultMapping,
  updateParameter,
  removeParameter,
  removeParameterMapping,
} from '../../../utils/redux/slices/webhookFunctionSlice';
import { RootState } from '../../../utils/redux';
import DeleteModal from '../../modals/DeleteModal';
import theme from '../../../theme';

const FunctionBuilder = () => {
  const dispatch = useDispatch();
  const {
    functionName,
    functionDescription,
    parameters,
    parameterMappings,
    apiConfig,
    resultMappings,
  } = useSelector((state: RootState) => state.webhookFunction);

  const [isAddingParam, setIsAddingParam] = useState(false);
  const [isEditingParam, setIsEditingParam] = useState(false);
  const [editingParamIndex, setEditingParamIndex] = useState<number>(-1);
  const [newParam, setNewParam] = useState<
    FunctionParameter & {
      items?: {
        name: string;
        type: string;
        properties: { name: string; type: ParameterType }[];
      };
    }
  >({
    name: '',
    type: 'string',
    description: '',
    required: false,
    items: undefined,
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingParamIndex, setDeletingParamIndex] = useState<number>(-1);
  const [isDeletingParam, setIsDeletingParam] = useState(false);
  const [nestedProperties, setNestedProperties] = useState<
    {
      name: string;
      type: ParameterType;
      description: string;
      required: boolean;
    }[]
  >([]);
  const [isAddingProperty, setIsAddingProperty] = useState(false);
  const [isEditingProperty, setIsEditingProperty] = useState(false);
  const [editingPropertyIndex, setEditingPropertyIndex] = useState<number>(-1);
  const [newProperty, setNewProperty] = useState<{
    name: string;
    type: ParameterType;
    description: string;
    required: boolean;
  }>({
    name: '',
    type: 'string',
    description: '',
    required: false,
  });
  const [deletingPropertyIndex, setDeletingPropertyIndex] =
    useState<number>(-1);

  const handleAddParameter = () => {
    setIsAddingParam(true);
    setIsEditingParam(false);
    // Reset parameter form
    setNewParam({
      name: '',
      type: 'string',
      description: '',
      required: false,
      items: undefined,
    });
  };

  const handleSaveParameter = () => {
    // Create the parameter to save
    const paramToSave: FunctionParameter = {
      name: newParam.name,
      type: newParam.type,
      description: newParam.description,
      required: newParam.required,
    };

    // For array type, properly structure the arrayItems property
    if (newParam.type === 'array' && newParam.items) {
      // Create properties object for the arrayItems
      const properties: Record<
        string,
        {
          type: 'string' | 'number' | 'boolean';
          description?: string;
          required?: boolean;
        }
      > = {};

      // Get required property names
      const requiredProps: string[] = [];

      // Add each nested property to the properties object
      if (nestedProperties.length > 0) {
        nestedProperties.forEach(prop => {
          properties[prop.name] = {
            type: prop.type as 'string' | 'number' | 'boolean',
            description: prop.description || `${prop.name} property`,
          };

          // Add to required array if this property is required
          if (prop.required) {
            requiredProps.push(prop.name);
          }
        });
      }

      // Add arrayItems to the parameter with the itemsName
      paramToSave.arrayItems = {
        type: 'object',
        itemsName: newParam.items.name,
        properties: properties,
        required: requiredProps,
      };
    }

    if (isEditingParam && editingParamIndex >= 0) {
      // Update existing parameter
      dispatch(
        updateParameter({
          index: editingParamIndex,
          parameter: paramToSave,
        })
      );
      setEditingParamIndex(-1);
      setIsEditingParam(false);
    } else {
      // Add new parameter
      dispatch(addParameter(paramToSave));
      setIsAddingParam(false);
    }

    // Reset form state
    setNewParam({
      name: '',
      type: 'string',
      description: '',
      required: false,
      items: undefined,
    });
    // Clear nested properties
    setNestedProperties([]);
  };

  const handleCancelParameter = () => {
    setIsAddingParam(false);
    setIsEditingParam(false);
    setEditingParamIndex(-1);
    setNewParam({
      name: '',
      type: 'string',
      description: '',
      required: false,
      items: undefined,
    });
  };

  const handleEditParameter = (index: number) => {
    // Load parameter data into the form
    if (index >= 0 && index < parameters.length) {
      const paramToEdit = parameters[index];

      // Initialize the form with basic parameter data
      const editParam: FunctionParameter & {
        items?: {
          name: string;
          type: string;
          properties: { name: string; type: ParameterType }[];
        };
      } = {
        name: paramToEdit.name,
        type: paramToEdit.type,
        description: paramToEdit.description,
        required: paramToEdit.required,
        items: undefined,
      };

      // Handle array type with nested properties
      if (paramToEdit.type === 'array' && paramToEdit.arrayItems) {
        // Set up the items object
        editParam.items = {
          name: paramToEdit.arrayItems.itemsName || paramToEdit.name + 'Item', // Use actual name or default
          type: 'object',
          properties: [],
        };

        // Extract nested properties from arrayItems to populate the UI
        const properties: {
          name: string;
          type: ParameterType;
          description: string;
          required: boolean;
        }[] = [];

        // Convert properties object to array format for UI
        if (paramToEdit.arrayItems.properties) {
          Object.entries(paramToEdit.arrayItems.properties).forEach(
            ([key, value]) => {
              properties.push({
                name: key,
                type: value.type as ParameterType,
                description: value.description || '',
                required:
                  paramToEdit.arrayItems?.required?.includes(key) || false,
              });
            }
          );
        }

        // Set the nested properties for the UI
        setNestedProperties(properties);
      }

      // Update the form state
      setNewParam(editParam);
      setEditingParamIndex(index);
      setIsEditingParam(true);
      setIsAddingParam(false);
    }
  };

  const handleDeleteParameter = (index: number) => {
    setDeletingParamIndex(index);
    setDeleteModalOpen(true);
  };

  const confirmDeleteParameter = () => {
    setIsDeletingParam(true);
    if (deletingParamIndex >= 0) {
      // Get the parameter being deleted
      const paramToDelete = parameters[deletingParamIndex];

      // Remove the parameter from the state
      dispatch(removeParameter(deletingParamIndex));

      // Also remove any associated parameter mappings
      const mappingIndexToRemove = parameterMappings.findIndex(
        mapping => mapping.functionParameter === paramToDelete.name
      );

      if (mappingIndexToRemove >= 0) {
        dispatch(removeParameterMapping(mappingIndexToRemove));
      }

      setIsDeletingParam(false);
      setDeleteModalOpen(false);
      setDeletingParamIndex(-1);
    }
  };

  const handleAddProperty = () => {
    setIsAddingProperty(true);
    setIsEditingProperty(false);
    setNewProperty({
      name: '',
      type: 'string',
      description: '',
      required: false,
    });
  };

  const handleSaveProperty = () => {
    if (isEditingProperty && editingPropertyIndex >= 0) {
      // Update existing property
      const updatedProperties = [...nestedProperties];
      updatedProperties[editingPropertyIndex] = newProperty;
      setNestedProperties(updatedProperties);
      setEditingPropertyIndex(-1);
      setIsEditingProperty(false);
    } else {
      // Add new property
      setNestedProperties([...nestedProperties, newProperty]);
    }

    // Update the newParam with these properties
    setNewParam({
      ...newParam,
      items: newParam.items
        ? {
            ...newParam.items,
            properties: isEditingProperty
              ? nestedProperties.map((prop, index) =>
                  index === editingPropertyIndex ? newProperty : prop
                )
              : [...nestedProperties, newProperty],
          }
        : {
            name: newParam.name + 'Item',
            type: 'object',
            properties: [...nestedProperties, newProperty],
          },
    });

    // Reset form
    setIsAddingProperty(false);
    setNewProperty({
      name: '',
      type: 'string',
      description: '',
      required: false,
    });
  };

  const handleCancelProperty = () => {
    setIsAddingProperty(false);
    setIsEditingProperty(false);
    setEditingPropertyIndex(-1);
    setNewProperty({
      name: '',
      type: 'string',
      description: '',
      required: false,
    });
  };

  const handleEditProperty = (index: number) => {
    if (index >= 0 && index < nestedProperties.length) {
      setNewProperty(nestedProperties[index]);
      setEditingPropertyIndex(index);
      setIsEditingProperty(true);
      setIsAddingProperty(false);
    }
  };

  const handleDeleteProperty = (index: number) => {
    setDeletingPropertyIndex(index);
    // We can reuse the same delete modal by setting a flag
    // or you can create a separate modal for properties
    setDeleteModalOpen(true);
  };

  const confirmDeleteProperty = () => {
    if (deletingPropertyIndex >= 0) {
      const updatedProperties = nestedProperties.filter(
        (_, index) => index !== deletingPropertyIndex
      );
      setNestedProperties(updatedProperties);

      // Update the newParam with these properties
      setNewParam({
        ...newParam,
        items: newParam.items
          ? {
              ...newParam.items,
              properties: updatedProperties,
            }
          : undefined,
      });

      setDeleteModalOpen(false);
      setDeletingPropertyIndex(-1);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        backgroundColor: '#FFFFFF',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          width: '589px',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            width: '114px',
            fontWeight: 400,
            fontSize: '12px',
            color: '#6B6F80',
          }}
        >
          Function Name
        </Typography>
        <TextField
          fullWidth
          value={functionName}
          onChange={e => dispatch(setFunctionName(e.target.value))}
          placeholder="Name of the function i.e. {search_movies}"
          sx={{
            width: '459px',
            height: '40px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderWidth: '1px',
                borderColor: '#DCE0E7',
                '&:hover': {
                  borderColor: '#DCE0E7',
                },
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px 12px',
              gap: '10px',
              backgroundColor: '#FFFFFF',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '-1%',
              color: '#302B3F',
              '&::placeholder': {
                color: '#6B6F80',
              },
            },
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          width: '589px',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            width: '114px',
            fontWeight: 400,
            fontSize: '12px',
            color: '#6B6F80',
            textWrap: 'nowrap',
          }}
        >
          Function Description
        </Typography>
        <TextField
          fullWidth
          value={functionDescription}
          onChange={e => dispatch(setFunctionDescription(e.target.value))}
          placeholder="Description of the function i.e. {search for movies by title}"
          sx={{
            width: '459px',
            height: '40px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderWidth: '1px',
                borderColor: '#DCE0E7',
                '&:hover': {
                  borderColor: '#DCE0E7',
                },
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px 12px',
              gap: '10px',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '-1%',
              color: '#302B3F',
              backgroundColor: '#FFFFFF',
              '&::placeholder': {
                color: '#6B6F80',
              },
            },
          }}
        />
      </Box>

      {/* Parameters Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                color: '#302B3F',
              }}
            >
              Parameters
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                color: '#6B6F80',
              }}
            >
              Define the parameters your function accepts
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<Plus size={16} />}
            disabled={isAddingParam}
            onClick={handleAddParameter}
            sx={{
              fontSize: 14,
              width: 'fit-content',
              height: '34px',
              fontWeight: 500,
              borderRadius: '6px',
              textTransform: 'none',
              boxShadow: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            Add Parameter
          </Button>
        </Box>

        {isAddingParam || isEditingParam ? (
          <Box
            sx={{
              backgroundColor: '#F8F8F8',
              borderRadius: '6px',
              padding: '16px',
              width: '100%',
              border: '1px solid #DCE0E7',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 2,
                '& > div': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  height: '40px',
                },
                '& .MuiTypography-root': {
                  width: '114px',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: '#6B6F80',
                },
              }}
            >
              {/* Name & Type field */}
              <Box>
                <Typography>Name & Type</Typography>
                <TextField
                  value={newParam.name}
                  onChange={e =>
                    setNewParam({ ...newParam, name: e.target.value })
                  }
                  placeholder="title"
                  sx={{
                    width: '357px',
                    height: '40px',
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: '#DCE0E7',
                        '&:hover': {
                          borderColor: '#DCE0E7',
                        },
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#302B3F',
                      backgroundColor: '#FFFFFF',
                      '&::placeholder': {
                        color: '#6B6F80',
                      },
                    },
                  }}
                />
                <Select
                  value={newParam.type}
                  onChange={e =>
                    setNewParam({
                      ...newParam,
                      type: e.target.value as ParameterType,
                    })
                  }
                  sx={{
                    width: '100px',
                    height: '40px',
                    border: '1px solid #DCE0E7',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    boxShadow: 'none',
                    background: '#F8F9FC',
                    '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                      {
                        border: 'none',
                      },
                    '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                      {
                        border: 'none',
                      },
                    '& .MuiSelect-select': {
                      padding: '8px 12px',
                      paddingRight: '26px',
                      display: 'flex',
                      alignItems: 'center',
                      background: '#F8F9FC',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: 'normal',
                      letterSpacing: '-0.14px',
                      color: theme.palette.lightGray.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <MenuItem value="string">string</MenuItem>
                  <MenuItem value="number">number</MenuItem>
                  <MenuItem value="boolean">boolean</MenuItem>
                  <MenuItem value="object">object</MenuItem>
                  <MenuItem value="array">array</MenuItem>
                </Select>
              </Box>

              {/* Description field */}
              <Box>
                <Typography>Description</Typography>
                <TextField
                  value={newParam.description}
                  onChange={e =>
                    setNewParam({ ...newParam, description: e.target.value })
                  }
                  placeholder="Movie title to search for"
                  fullWidth
                  rows={1}
                  sx={{
                    width: '459px',
                    height: '40px',
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: '#DCE0E7',
                        '&:hover': {
                          borderColor: '#DCE0E7',
                        },
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#302B3F',
                      backgroundColor: '#FFFFFF',
                      '&::placeholder': {
                        color: '#6B6F80',
                      },
                    },
                  }}
                />
              </Box>

              {/* Required checkbox */}
              <Box>
                <Typography>Required</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newParam.required}
                      onChange={e =>
                        setNewParam({ ...newParam, required: e.target.checked })
                      }
                      sx={{
                        marginLeft: '8px',
                        width: '20px',
                        height: '20px',
                        color: 'transparent',
                        '&.Mui-checked': {
                          color: '#6366F1',
                        },
                        '& .MuiSvgIcon-root': {
                          borderRadius: '6px',
                          border: '1px solid #DCE0E7',
                          backgroundColor: '#FFFFFF',
                          width: '20px',
                          height: '20px',
                          '&:hover': {
                            borderColor: '#6366F1',
                          },
                        },
                      }}
                    />
                  }
                  label=""
                  sx={{
                    marginLeft: '-11px',
                    '& .MuiFormControlLabel-label': {
                      display: 'none',
                    },
                  }}
                />
              </Box>

              {/* Add array items configuration UI after the main parameter fields */}
              {newParam.type === 'array' && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    pl: 2,
                    width: '100%',
                    height: 'auto !important', // Override the parent's fixed height
                  }}
                >
                  {/* Items Name & Type */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    <Typography sx={{ width: '100%' }}>Object Name</Typography>
                    <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                      <TextField
                        value={newParam.items?.name}
                        onChange={e =>
                          setNewParam({
                            ...newParam,
                            items: {
                              name: e.target.value,
                              type: newParam.items?.type || 'object',
                              properties: newParam.items?.properties || [],
                            },
                          })
                        }
                        placeholder="items"
                        sx={{
                          flex: 1,
                          height: '40px',
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            borderRadius: '6px',
                            backgroundColor: '#FFFFFF',
                            '& fieldset': {
                              borderWidth: '1px',
                              borderColor: '#DCE0E7',
                              '&:hover': {
                                borderColor: '#DCE0E7',
                              },
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            padding: '8px 12px',
                            fontSize: '14px',
                            color: '#302B3F',
                            backgroundColor: '#FFFFFF',
                            '&::placeholder': {
                              color: '#6B6F80',
                            },
                          },
                        }}
                      />
                      <Select
                        value={newParam.items?.type || 'object'}
                        onChange={e =>
                          setNewParam({
                            ...newParam,
                            items: {
                              name: newParam.items?.name || 'items',
                              type: e.target.value as ParameterType,
                              properties: newParam.items?.properties || [],
                            },
                          })
                        }
                        sx={{
                          width: '100px',
                          height: '40px',
                          border: '1px solid #DCE0E7',
                          borderRadius: '6px',
                          overflow: 'hidden',
                          boxShadow: 'none',
                          background: '#F8F9FC',
                          '.MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                            {
                              border: 'none',
                            },
                          '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                            {
                              border: 'none',
                            },
                          '& .MuiSelect-select': {
                            padding: '8px 12px',
                            paddingRight: '26px',
                            display: 'flex',
                            alignItems: 'center',
                            background: '#F8F9FC',
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: 'normal',
                            letterSpacing: '-0.14px',
                            color: theme.palette.lightGray.text,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      >
                        <MenuItem value="object">object</MenuItem>
                      </Select>
                    </Box>
                  </Box>

                  {/* Show Object Properties section */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      ml: 2,
                      mt: 1,
                      borderLeft: '2px solid #DCE0E7',
                      pl: 2,
                      width: '100%',
                      height: 'auto !important',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#302B3F',
                          width: 'auto',
                        }}
                      >
                        Object Properties
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        startIcon={<Plus size={14} />}
                        onClick={handleAddProperty}
                        disabled={isAddingProperty || isEditingProperty}
                        sx={{
                          fontSize: 12,
                          width: 'fit-content',
                          fontWeight: 500,
                          textTransform: 'none',
                        }}
                      >
                        Add Property
                      </Button>
                    </Box>

                    {/* Property form */}
                    {isAddingProperty || isEditingProperty ? (
                      <Box
                        sx={{
                          backgroundColor: '#F0F0F0',
                          borderRadius: '6px',
                          padding: '16px',
                          width: '100%',
                          border: '1px solid #DCE0E7',
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: 2,
                            '& > div': {
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              height: '40px',
                            },
                            '& .MuiTypography-root': {
                              width: '114px',
                              fontWeight: 400,
                              fontSize: '12px',
                              color: '#6B6F80',
                            },
                          }}
                        >
                          {/* Name & Type field */}
                          <Box>
                            <Typography>Name & Type</Typography>
                            <TextField
                              value={newProperty.name}
                              onChange={e =>
                                setNewProperty({
                                  ...newProperty,
                                  name: e.target.value,
                                })
                              }
                              placeholder="title"
                              sx={{
                                width: '250px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiOutlinedInput-input': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                  '&::placeholder': {
                                    color: '#6B6F80',
                                  },
                                },
                              }}
                            />
                            <Select
                              value={newProperty.type}
                              onChange={e =>
                                setNewProperty({
                                  ...newProperty,
                                  type: e.target.value as ParameterType,
                                })
                              }
                              sx={{
                                width: '100px',
                                height: '40px',
                                border: '1px solid #DCE0E7',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                boxShadow: 'none',
                                background: '#F8F9FC',
                                '.MuiOutlinedInput-notchedOutline': {
                                  border: 'none',
                                },
                                '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                                  {
                                    border: 'none',
                                  },
                                '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  {
                                    border: 'none',
                                  },
                                '& .MuiSelect-select': {
                                  display: 'flex',
                                  paddingRight: '26px',
                                  padding: '8px 12px',
                                  alignItems: 'center',
                                  background: '#F8F9FC',
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  lineHeight: 'normal',
                                  letterSpacing: '-0.14px',
                                  color: theme.palette.lightGray.text,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                },
                              }}
                            >
                              <MenuItem value="string">string</MenuItem>
                              <MenuItem value="number">number</MenuItem>
                              <MenuItem value="boolean">boolean</MenuItem>
                            </Select>
                          </Box>

                          {/* Description field */}
                          <Box>
                            <Typography>Description</Typography>
                            <TextField
                              value={newProperty.description}
                              onChange={e =>
                                setNewProperty({
                                  ...newProperty,
                                  description: e.target.value,
                                })
                              }
                              placeholder="Property description"
                              fullWidth
                              rows={1}
                              sx={{
                                width: '350px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiOutlinedInput-input': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                  '&::placeholder': {
                                    color: '#6B6F80',
                                  },
                                },
                              }}
                            />
                          </Box>

                          {/* Required checkbox */}
                          <Box>
                            <Typography>Required</Typography>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={newProperty.required}
                                  onChange={e =>
                                    setNewProperty({
                                      ...newProperty,
                                      required: e.target.checked,
                                    })
                                  }
                                  sx={{
                                    marginLeft: '8px',
                                    width: '20px',
                                    height: '20px',
                                    color: 'transparent',
                                    '&.Mui-checked': {
                                      color: '#6366F1',
                                    },
                                    '& .MuiSvgIcon-root': {
                                      borderRadius: '6px',
                                      border: '1px solid #DCE0E7',
                                      backgroundColor: '#FFFFFF',
                                      width: '20px',
                                      height: '20px',
                                      '&:hover': {
                                        borderColor: '#6366F1',
                                      },
                                    },
                                  }}
                                />
                              }
                              label=""
                              sx={{
                                marginLeft: '-11px',
                                '& .MuiFormControlLabel-label': {
                                  display: 'none',
                                },
                              }}
                            />
                          </Box>

                          {/* Cancel & Save Buttons */}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              gap: 2,
                              marginTop: 1,
                              height: 'auto !important',
                            }}
                          >
                            <Button
                              onClick={handleCancelProperty}
                              sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                textTransform: 'none',
                                color: '#6B6F80',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: '#302B3F',
                                },
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleSaveProperty}
                              disabled={!newProperty.name}
                              sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                textTransform: 'none',
                                backgroundColor: '#6366F1',
                                borderRadius: '6px',
                                '&:hover': {
                                  backgroundColor: '#4F46E5',
                                },
                                '&.Mui-disabled': {
                                  backgroundColor: '#E4E7EC',
                                  color: '#9DA4AE',
                                },
                              }}
                            >
                              {isEditingProperty
                                ? 'Update Property'
                                : 'Save Property'}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        {/* Display properties list */}
                        {nestedProperties.length > 0 ? (
                          <Box sx={{ mb: 2 }}>
                            {nestedProperties.map((property, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: 'flex',
                                  height: '40px',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '12px 16px',
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '8px',
                                  border: '1px solid #DCE0E7',
                                  mb: 1,
                                  width: '100%',
                                  mr: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 400,
                                      fontSize: '12px',
                                      color: '#6B6F80',
                                    }}
                                  >
                                    {property.type}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '14px',
                                      color: '#302B3F',
                                    }}
                                  >
                                    {property.name}
                                    {property.required && (
                                      <span
                                        style={{
                                          color: '#EF4444',
                                          fontSize: '12px',
                                        }}
                                      >
                                        *
                                      </span>
                                    )}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <IconButton
                                    onClick={() => handleEditProperty(index)}
                                  >
                                    <PencilSimple size={16} />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => handleDeleteProperty(index)}
                                  >
                                    <TrashSimple
                                      size={16}
                                      weight="fill"
                                      color="#E74C3C"
                                    />
                                  </IconButton>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              color: '#6B6F80',
                              fontSize: '12px',
                              fontStyle: 'italic',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            No properties defined. Click "Add Property" to add
                            object properties.
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              )}

              {/* Cancel & Save Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  marginTop: 1,
                  height: 'auto !important', // Override the previous height
                }}
              >
                <Button
                  onClick={handleCancelParameter}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    color: '#6B6F80',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#302B3F',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveParameter}
                  disabled={!newParam.name}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    backgroundColor: '#6366F1',
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: '#4F46E5',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#E4E7EC',
                      color: '#9DA4AE',
                    },
                  }}
                >
                  {isEditingParam ? 'Update Parameter' : 'Save Parameter'}
                </Button>
              </Box>
            </Box>
          </Box>
        ) : parameters.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              backgroundColor: '#F8F8F8',
              padding: '12px 16px',
              borderRadius: '6px',
              border: '1px solid #DCE0E7',
            }}
          >
            {parameters.map((param: any, index: number) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  height: '40px',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                  border: '1px solid #DCE0E7',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '12px',
                      color: '#6B6F80',
                    }}
                  >
                    {param.type}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#302B3F',
                      }}
                    >
                      {param.name}
                    </Typography>
                    {param.required && (
                      <Tooltip title="Required">
                        <Typography
                          sx={{
                            color: '#EF4444',
                            fontSize: '14px',
                            marginLeft: '2px',
                          }}
                        >
                          *
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton onClick={() => handleEditParameter(index)}>
                    <PencilSimple size={16} />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteParameter(index)}>
                    <TrashSimple size={16} weight="fill" color="#E74C3C" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#F8F8F8',
                borderRadius: '6px',
                padding: '16px',
                width: '100%',
                textAlign: 'center',
                color: '#6B6F80',
                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              No parameters added. Click "Add Parameter" to add your first
              parameter
            </Box>
          </Box>
        )}
      </Box>

      {/* API Configuration Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#302B3F',
            }}
          >
            API Configuration
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '12px',
              color: '#6B6F80',
            }}
          >
            Configure API calling for your function
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            backgroundColor: '#F8F8F8',
            borderRadius: '6px',
            padding: '16px 8px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                width: '114px',
                fontWeight: 400,
                fontSize: '12px',
                color: '#6B6F80',
              }}
            >
              API Endpoint
            </Typography>
            <Box
              sx={{
                display: 'flex',
                width: '459px',
                border: '1px solid #DCE0E7',
                borderRadius: '6px',
                overflow: 'hidden',
                height: '40px',
              }}
            >
              <Select
                value={apiConfig.method}
                onChange={e =>
                  dispatch(
                    setApiMethod(
                      e.target.value as 'GET' | 'POST' | 'PUT' | 'DELETE'
                    )
                  )
                }
                sx={{
                  minWidth: '80px',
                  height: '40px',
                  borderRight: '1px solid #DCE0E7',
                  boxShadow: 'none',
                  background: '#F8F9FC',
                  '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                    {
                      border: 'none',
                    },
                  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                    {
                      border: 'none',
                    },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    padding: '8px 12px',
                    paddingRight: '26px',
                    alignItems: 'center',
                    background: '#F8F9FC',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    letterSpacing: '-0.14px',
                    color: theme.palette.lightGray.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
              <TextField
                value={apiConfig.endpoint}
                onChange={e => dispatch(setApiEndpoint(e.target.value))}
                placeholder="Enter url"
                variant="standard"
                sx={{
                  width: 'calc(100% - 80px)',
                  height: '40px',
                  backgroundColor: '#FFFFFF',
                  '& .MuiInput-root': {
                    height: '100%',
                    backgroundColor: '#FFFFFF',
                    '&:before, &:after': {
                      display: 'none',
                    },
                  },
                  '& .MuiInput-input': {
                    height: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#302B3F',
                    backgroundColor: '#FFFFFF',
                    '&::placeholder': {
                      color: '#6B6F80',
                    },
                  },
                }}
                InputProps={{
                  disableUnderline: true,
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                width: '114px',
                fontWeight: 400,
                fontSize: '12px',
                color: '#6B6F80',
              }}
            >
              API Key
            </Typography>
            <Box sx={{ display: 'flex', gap: '16px', width: '459px' }}>
              <TextField
                value={apiConfig.queryParams[0]?.key || ''}
                onChange={e => {
                  if (apiConfig.queryParams.length > 0) {
                    dispatch(
                      updateQueryParam({
                        index: 0,
                        queryParam: {
                          key: e.target.value,
                          value: apiConfig.queryParams[0]?.value || '',
                        },
                      })
                    );
                  } else {
                    dispatch(
                      addQueryParam({
                        key: e.target.value,
                        value: '',
                      })
                    );
                  }
                }}
                placeholder="API Key name"
                sx={{
                  width: '50%',
                  height: '40px',
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                      '&:hover': {
                        borderColor: '#DCE0E7',
                      },
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#302B3F',
                    backgroundColor: '#FFFFFF',
                    '&::placeholder': {
                      color: '#6B6F80',
                    },
                  },
                }}
              />
              <TextField
                value={apiConfig.queryParams[0]?.value || ''}
                onChange={e => {
                  if (apiConfig.queryParams.length > 0) {
                    dispatch(
                      updateQueryParam({
                        index: 0,
                        queryParam: {
                          key: apiConfig.queryParams[0]?.key || '',
                          value: e.target.value,
                        },
                      })
                    );
                  } else {
                    dispatch(
                      addQueryParam({
                        key: '',
                        value: e.target.value,
                      })
                    );
                  }
                }}
                placeholder="Value"
                sx={{
                  width: '50%',
                  height: '40px',
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: '#DCE0E7',
                      '&:hover': {
                        borderColor: '#DCE0E7',
                      },
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: '#302B3F',
                    backgroundColor: '#FFFFFF',
                    '&::placeholder': {
                      color: '#6B6F80',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                width: '114px',
                fontWeight: 400,
                fontSize: '12px',
                color: '#6B6F80',
                textWrap: 'nowrap',
              }}
            >
              Processing Message
            </Typography>
            <TextField
              value={apiConfig.processingMessage}
              onChange={e => dispatch(setProcessingMessage(e.target.value))}
              placeholder="Enter processing message"
              sx={{
                width: '459px',
                height: '40px',
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF',
                  '& fieldset': {
                    borderWidth: '1px',
                    borderColor: '#DCE0E7',
                    '&:hover': {
                      borderColor: '#DCE0E7',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  fontSize: '14px',
                  color: '#302B3F',
                  backgroundColor: '#FFFFFF',
                  '&::placeholder': {
                    color: '#6B6F80',
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Parameter Mapping Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#302B3F',
            }}
          >
            Parameter Mapping
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '12px',
              color: '#6B6F80',
            }}
          >
            Map Function parameters to your API parameters
          </Typography>
        </Box>

        {parameters.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              backgroundColor: '#F8F8F8',
              borderRadius: '6px',
              padding: '16px 8px',
              width: '100%',
            }}
          >
            {/* Mapping Table Header */}
            <Box
              sx={{
                display: 'flex',
                gap: '16px',
                alignItems: 'center',
                paddingX: '8px',
              }}
            >
              <Typography
                sx={{
                  width: '230px',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: '#6B6F80',
                }}
              >
                Function Parameter
              </Typography>
              <Typography
                sx={{
                  width: '226px',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: '#6B6F80',
                }}
              >
                API Parameter
              </Typography>
              <Typography
                sx={{
                  width: '226px',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: '#6B6F80',
                }}
              >
                Value
              </Typography>
              <Typography
                sx={{
                  width: '217px',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: '#6B6F80',
                }}
              >
                Location
              </Typography>
            </Box>

            {/* Individual parameter mapping rows - one per parameter */}
            {parameters.map((param: FunctionParameter, index: number) => {
              // Find existing mapping for this parameter
              const existingMapping: ParameterMapping | undefined =
                parameterMappings.find(
                  mapping => mapping.functionParameter === param.name
                );

              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'center',
                    paddingX: '8px',
                    marginBottom: '8px',
                  }}
                >
                  <Box
                    sx={{
                      width: '230px',
                      height: '40px',
                      border: '1px solid #DCE0E7',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      paddingX: '12px',
                      fontSize: '14px',
                      color: '#302B3F',
                    }}
                  >
                    {param.name}
                  </Box>
                  <TextField
                    placeholder="Parameter Name"
                    value={existingMapping?.apiParameter || ''}
                    onChange={e => {
                      if (existingMapping) {
                        // Find the index of the existing mapping
                        const mappingIndex = parameterMappings.findIndex(
                          m => m.functionParameter === param.name
                        );

                        dispatch(
                          updateParameterMapping({
                            index: mappingIndex,
                            mapping: {
                              ...existingMapping,
                              apiParameter: e.target.value,
                            },
                          })
                        );
                      } else {
                        dispatch(
                          addParameterMapping({
                            functionParameter: param.name,
                            apiParameter: e.target.value,
                            location: 'params',
                          })
                        );
                      }
                    }}
                    sx={{
                      width: '226px',
                      height: '40px',
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        borderRadius: '6px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderWidth: '1px',
                          borderColor: '#DCE0E7',
                          '&:hover': {
                            borderColor: '#DCE0E7',
                          },
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                        fontSize: '14px',
                        color: '#302B3F',
                        backgroundColor: '#FFFFFF',
                        '&::placeholder': {
                          color: '#6B6F80',
                        },
                      },
                    }}
                  />
                  <TextField
                    placeholder="Value"
                    value={existingMapping?.value || ''}
                    onChange={e => {
                      if (existingMapping) {
                        const mappingIndex = parameterMappings.findIndex(
                          m => m.functionParameter === param.name
                        );
                        dispatch(
                          updateParameterMapping({
                            index: mappingIndex,
                            mapping: {
                              ...existingMapping,
                              value: e.target.value,
                            },
                          })
                        );
                      }
                    }}
                    sx={{
                      width: '226px',
                      height: '40px',
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        borderRadius: '6px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderWidth: '1px',
                          borderColor: '#DCE0E7',
                          '&:hover': {
                            borderColor: '#DCE0E7',
                          },
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                        fontSize: '14px',
                        color: '#302B3F',
                        backgroundColor: '#FFFFFF',
                        '&::placeholder': {
                          color: '#6B6F80',
                        },
                      },
                    }}
                  />
                  <Select
                    value={existingMapping?.location || 'params'}
                    onChange={e => {
                      if (existingMapping) {
                        // Find the index of the existing mapping
                        const mappingIndex = parameterMappings.findIndex(
                          m => m.functionParameter === param.name
                        );

                        dispatch(
                          updateParameterMapping({
                            index: mappingIndex,
                            mapping: {
                              ...existingMapping,
                              location: e.target.value as
                                | 'params'
                                | 'body'
                                | 'headers'
                                | 'path',
                            },
                          })
                        );
                      } else {
                        dispatch(
                          addParameterMapping({
                            functionParameter: param.name,
                            apiParameter: e.target.value,
                            location: e.target.value as
                              | 'params'
                              | 'body'
                              | 'headers'
                              | 'path',
                          })
                        );
                      }
                    }}
                    sx={{
                      width: '217px',
                      height: '40px',
                      border: '1px solid #DCE0E7',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      boxShadow: 'none',
                      backgroundColor: '#FFFFFF',
                      '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                        {
                          border: 'none',
                        },
                      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                        {
                          border: 'none',
                        },
                      '& .MuiSelect-select': {
                        display: 'flex',
                        padding: '8px 12px',
                        paddingRight: '26px',
                        alignItems: 'center',
                        background: '#F8F9FC',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: 'normal',
                        letterSpacing: '-0.14px',
                        color: theme.palette.lightGray.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    <MenuItem value="params">params</MenuItem>
                    <MenuItem value="body">body</MenuItem>
                    <MenuItem value="headers">headers</MenuItem>
                    <MenuItem value="path">path</MenuItem>
                  </Select>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: '#F9FAFB',
              borderRadius: '6px',
              padding: '16px',
              width: '100%',
              textAlign: 'center',
              color: '#6B6F80',
              fontSize: '12px',
              fontWeight: 400,
            }}
          >
            No parameters added. Create function parameter to map with API
            parameter
          </Box>
        )}
      </Box>

      {/* Result Mapping Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                color: '#302B3F',
              }}
            >
              Result Mapping
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                color: '#6B6F80',
              }}
            >
              Map Result to your API response
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<Plus size={16} />}
            onClick={() =>
              dispatch(
                addResultMapping({ resultField: '', apiResponsePath: '' })
              )
            }
            sx={{
              fontSize: 14,
              width: 'fit-content',
              height: '34px',
              fontWeight: 500,
              borderRadius: '6px',
              textTransform: 'none',
              boxShadow: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            Add Field
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            backgroundColor: '#F8F8F8',
            borderRadius: '6px',
            padding: '16px 8px',
          }}
        >
          {resultMappings.length > 0 ? (
            resultMappings.map((mapping, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    width: '50%',
                  }}
                >
                  {index === 0 && (
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: '12px',
                        color: '#6B6F80',
                      }}
                    >
                      Result Field
                    </Typography>
                  )}
                  <TextField
                    placeholder="Result Field Name"
                    value={mapping.resultField || ''}
                    onChange={e => {
                      dispatch(
                        updateResultMapping({
                          index,
                          mapping: {
                            resultField: e.target.value,
                            apiResponsePath: mapping.apiResponsePath || '',
                          },
                        })
                      );
                    }}
                    sx={{
                      width: '100%',
                      height: '40px',
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        borderRadius: '6px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderWidth: '1px',
                          borderColor: '#DCE0E7',
                          '&:hover': {
                            borderColor: '#DCE0E7',
                          },
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                        fontSize: '14px',
                        color: '#302B3F',
                        backgroundColor: '#FFFFFF',
                        '&::placeholder': {
                          color: '#6B6F80',
                        },
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    width: '50%',
                  }}
                >
                  {index === 0 && (
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: '12px',
                        color: '#6B6F80',
                      }}
                    >
                      API Response Path
                    </Typography>
                  )}
                  <TextField
                    placeholder="API Response Path"
                    value={mapping.apiResponsePath || ''}
                    onChange={e => {
                      dispatch(
                        updateResultMapping({
                          index,
                          mapping: {
                            resultField: mapping.resultField || '',
                            apiResponsePath: e.target.value,
                          },
                        })
                      );
                    }}
                    sx={{
                      width: '100%',
                      height: '40px',
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        borderRadius: '6px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderWidth: '1px',
                          borderColor: '#DCE0E7',
                          '&:hover': {
                            borderColor: '#DCE0E7',
                          },
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                        fontSize: '14px',
                        color: '#302B3F',
                        backgroundColor: '#FFFFFF',
                        '&::placeholder': {
                          color: '#6B6F80',
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            ))
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                color: '#6B6F80',
                fontSize: '12px',
                fontWeight: 400,
                padding: '8px',
              }}
            >
              No result mappings added. Click "Add Field" to add your first
              mapping.
            </Box>
          )}
        </Box>
      </Box>
      <DeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={
          deletingPropertyIndex >= 0
            ? confirmDeleteProperty
            : confirmDeleteParameter
        }
        isPending={isDeletingParam}
        title={
          deletingPropertyIndex >= 0 ? 'Remove Property' : 'Remove Parameter'
        }
        description={`Are you sure you want to remove this ${deletingPropertyIndex >= 0 ? 'property' : 'parameter'}? This action cannot be undone.`}
        confirmButtonText={
          deletingPropertyIndex >= 0 ? 'Remove Property' : 'Remove Parameter'
        }
        cancelButtonText="Cancel"
      />
    </Box>
  );
};

export default FunctionBuilder;
